import React, { useState, useRef, useEffect } from "react";
import googleTransliterate from "google-input-tool";

const TransliterationTextBox = ({value, onChange, placeholder }) => {
  const [text, setText] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [cursorPosition, setCursorPosition] = useState({ top: 0, left: 0 });
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [transliterationEnabled, setTransliterationEnabled] = useState(true);
  const textAreaRef = useRef(null);
  const suggestionBoxRef = useRef(null);
  const lastKeyPressTime = useRef(0);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    setText(value);
  }, [value]);

  const getCurrentWord = (inputText, cursorIndex) => {
    const words = inputText.substring(0, cursorIndex).split(/\s+/);
    return words[words.length - 1] || "";
  };

  const isNumeric = (str) => {
    return /^\d+$/.test(str);
  };

  const fetchTransliteration = async (inputText, cursorIndex) => {
    // Skip transliteration if disabled
    if (!transliterationEnabled) {
      setSuggestions([]);
      return;
    }
    
    const currentWord = getCurrentWord(inputText, cursorIndex).trim();
    
    if (currentWord.length > 0) {
      // Check if the current word is a number
      if (isNumeric(currentWord)) {
        // For numbers, just show the same number as suggestion
        setSuggestions([currentWord]);
        return;
      }
      
      try {
        let response = await googleTransliterate(
          new XMLHttpRequest(),
          currentWord,
          "te-t-i0-und",
          5
        );
        const processed = response.map((suggestion) => 
          typeof suggestion === "string" ? suggestion : suggestion.text || suggestion.toString()
        ).map(s => s.split(",")[0]);
        setSuggestions(processed.filter(s => s !== currentWord));
      } catch (error) {
        console.error("Error fetching transliteration:", error);
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
    }
  };

  const calculateCursorPosition = () => {
    if (!textAreaRef.current) return;

    const textarea = textAreaRef.current;
    const cursorIndex = textarea.selectionStart;
    
    const mirror = document.createElement('div');
    mirror.style.cssText = `
      position: absolute;
      visibility: hidden;
      height: auto;
      width: ${textarea.clientWidth}px;
      white-space: pre-wrap;
      overflow-wrap: break-word;
      word-wrap: break-word;
      font-family: ${window.getComputedStyle(textarea).fontFamily};
      font-size: ${window.getComputedStyle(textarea).fontSize};
      line-height: ${window.getComputedStyle(textarea).lineHeight};
      padding: ${window.getComputedStyle(textarea).padding};
    `;

    const textBeforeCursor = document.createElement('span');
    textBeforeCursor.textContent = text.substring(0, cursorIndex);
    
    const currentWordSpan = document.createElement('span');
    currentWordSpan.textContent = getCurrentWord(text, cursorIndex);
    
    mirror.appendChild(textBeforeCursor);
    mirror.appendChild(currentWordSpan);
    document.body.appendChild(mirror);

    const textareaRect = textarea.getBoundingClientRect();
    const currentWordRect = currentWordSpan.getBoundingClientRect();
    const scrollTop = textarea.scrollTop;
    
    const top = textareaRect.top + currentWordRect.height + 
                (currentWordRect.top - mirror.getBoundingClientRect().top) - 
                scrollTop;
    const left = textareaRect.left + 
                (currentWordRect.left - mirror.getBoundingClientRect().left);

    document.body.removeChild(mirror);

    return {
      top: Math.min(top, textareaRect.bottom - 150),
      left: Math.min(left, textareaRect.right - 220)
    };
  };

  const handleSuggestionClick = (word) => {
    const cursorIndex = textAreaRef.current.selectionStart;
    const beforeCursor = text.substring(0, cursorIndex);
    const afterCursor = text.substring(cursorIndex);
    const lastSeparatorIndex = Math.max(
      beforeCursor.lastIndexOf(" "),
      beforeCursor.lastIndexOf("\n")
    );
    
    const prefix = lastSeparatorIndex === -1 ? "" : beforeCursor.substring(0, lastSeparatorIndex + 1);
    const newText = prefix + word + afterCursor;
    setText(newText);
    onChange(newText);
    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
    
    const newCursorPosition = prefix.length + word.length;
    setTimeout(() => {
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (suggestions.length > 0) {
      switch (e.key) {
        case ' ':
          e.preventDefault();
          if (selectedSuggestionIndex !== -1) {
            handleSuggestionClick(suggestions[selectedSuggestionIndex] + ' ');
          } else {
            handleSuggestionClick(suggestions[0] + ' ');
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedSuggestionIndex(prev =>
            prev < suggestions.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedSuggestionIndex(prev =>
            prev > -1 ? prev - 1 : -1
          );
          break;
        case 'Enter':
          if (selectedSuggestionIndex !== -1) {
            e.preventDefault();
            handleSuggestionClick(suggestions[selectedSuggestionIndex]);
          }
          break;
        default:
          break;
      }
    }
  };

  const handleBeforeInput = (e) => {
    if (e.data === ' ' && suggestions.length > 0) {
      e.preventDefault();
      if (selectedSuggestionIndex !== -1) {
        handleSuggestionClick(suggestions[selectedSuggestionIndex] + ' ');
      } else {
        handleSuggestionClick(suggestions[0] + ' ');
      }
    }
  };

  const handleTextAreaChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    onChange(newText);
    setSelectedSuggestionIndex(-1);
    
    const cursorIndex = e.target.selectionStart;
    
    // Only fetch transliteration if enabled
    if (transliterationEnabled) {
      fetchTransliteration(newText, cursorIndex);
    }
    
    // Check if the last character typed was a space
    if (transliterationEnabled && newText.length > text.length && newText[cursorIndex - 1] === ' ') {
      const now = Date.now();
      // Prevent double triggering with beforeInput
      if (now - lastKeyPressTime.current > 100) {
        lastKeyPressTime.current = now;
        if (suggestions.length > 0) {
          // Remove the space that was just added
          const withoutSpace = newText.substring(0, cursorIndex - 1) + newText.substring(cursorIndex);
          setText(withoutSpace);
          onChange(withoutSpace);
          if (selectedSuggestionIndex !== -1) {
            handleSuggestionClick(suggestions[selectedSuggestionIndex] + ' ');
          } else {
            handleSuggestionClick(suggestions[0] + ' ');
          }
          return;
        }
      }
    }
    
    requestAnimationFrame(() => {
      const newPosition = calculateCursorPosition();
      if (newPosition) {
        setCursorPosition(newPosition);
      }
    });
  };

  const toggleTransliteration = () => {
    setTransliterationEnabled(!transliterationEnabled);
    // Clear suggestions when turning off
    if (transliterationEnabled) {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    const handleUpdate = () => {
      const newPosition = calculateCursorPosition();
      if (newPosition) {
        setCursorPosition(newPosition);
      }
    };

    const textarea = textAreaRef.current;
    window.addEventListener('resize', handleUpdate);
    textarea.addEventListener('scroll', handleUpdate);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      textarea.removeEventListener('scroll', handleUpdate);
    };
  }, [text]);

  return (
    <div className="relative w-full h-full flex flex-col">
      <textarea
        ref={textAreaRef}
        value={text}
        onChange={(e) => {
          handleTextAreaChange(e);
          setIsTyping(true);
          setTimeout(() => setIsTyping(false), 2000);
        }}
        onKeyDown={handleKeyDown}
        onBeforeInput={handleBeforeInput}
        placeholder={placeholder}
        className="flex-1 w-full h-[300px] p-4 bg-[#181A1B] text-[#EAECEF] 
                 border border-[#3A3D3E] rounded-lg 
                 focus:outline-none focus:ring-2 focus:ring-[#178A94]
                 text-base resize-none placeholder-[#7C8085]
                 scrollbar-thin scrollbar-thumb-[#178A94] scrollbar-track-[#2C2F30]"
      />
  
      {/* Toggle Switch - Moved to bottom right */}
      {/* Toggle Switch - Now inside a visible block */}
      <div className="absolute bottom-4 right-4 z-50 flex items-center gap-2 
                    bg-[#181A1B] px-3 py-1.5 rounded-md shadow-lg border border-[#3A3D3E]">
        <span className="text-xs text-[#EAECEF] font-medium">
          {transliterationEnabled ? "తెలుగు" : "En"}
        </span>
        <button
          onClick={toggleTransliteration}
          className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors 
                    focus:outline-none focus:ring-2 focus:ring-[#178A94] focus:ring-opacity-50
                    ${transliterationEnabled ? 'bg-[#178A94]' : 'bg-[#3A3D3E]'}`}
          role="switch"
          aria-checked={transliterationEnabled}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform
                      ${transliterationEnabled ? 'translate-x-5' : 'translate-x-1'}`}
          />
        </button>
      </div>

  
      {!isTyping && text && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 
                      flex items-center gap-2 bg-[#000000] 
                      px-3 py-1.5 md:px-4 md:py-2
                      rounded-md shadow-lg animate-pulse
                      max-w-[90%] md:max-w-none">
          <svg className="w-3 h-3 md:w-4 md:h-4 text-white rotate-180" 
               fill="none" 
               stroke="currentColor" 
               viewBox="0 0 24 24">
            <path strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M19 9l-7 7-7-7" />
          </svg>
          <span className="text-white font-bold text-xs md:text-sm whitespace-normal md:whitespace-nowrap">
            {window.innerWidth < 768 ? 'Type here' : 'Replace or type your script here. Once done, select a voice and hit generate.'}
          </span>
        </div>
      )}
      
      {suggestions.length > 0 && (
        <ul
          ref={suggestionBoxRef}
          style={{
            position: "fixed",
            top: cursorPosition.top + "px",
            left: cursorPosition.left + "px",
            maxHeight: "120px",
            width: "200px"
          }}
          className="bg-[#282C2F] border border-[#3A3D3E] rounded-lg
                    shadow-lg overflow-y-auto
                    scrollbar-thin scrollbar-thumb-[#178A94] scrollbar-track-[#2C2F30]
                    z-50"
        >
          {suggestions.map((word, index) => (
            <li
              key={index}
              className={`px-4 py-2 cursor-pointer border-b border-[#3A3D3E] 
                         last:border-b-0 text-[#EAECEF] text-sm transition-colors
                         ${index === selectedSuggestionIndex ? 'bg-[#178A94]' : 'hover:bg-[#3A3D3E]'}`}
              onClick={() => handleSuggestionClick(word)}
            >
              {word}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TransliterationTextBox;